import { addDays, startOfDay } from "@app/core";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSettingsStore, useTasksStore } from "../../stores/index.js";
import { useCalendarNavStore } from "../../stores/calendarNav.store.js";
import { useViewFilters } from "../../lib/useViewFilters.js";
import { useExpandedWindow } from "../../features/calendar-view/useExpandedWindow.js";
import { multiDayFocusAnchor } from "../../features/calendar-view/multi-day-focus.js";
import { useMultiDayFocusRollover } from "../../features/calendar-view/useMultiDayFocusRollover.js";
import { WeekGrid } from "../../features/calendar-view/WeekGrid.js";
import { CalendarHeader } from "../../features/calendar-view/CalendarHeader.js";
import { InboxPanel, InboxToggle } from "../../features/inbox/index.js";
import { MobileInboxDrawer } from "../../features/mobile/index.js";
import { useMobileUx } from "../../lib/useMobileUx.js";
import { useCalendarNav, useRememberedCalendarAnchor } from "./useCalendarNav.js";

/** Flexible "X-Days" view: shows `calendarDayCount` days from the anchor. */
export function DaysRoute() {
  const tasks = useTasksStore((s) => s.tasks);
  const rules = useTasksStore((s) => s.rules);
  const overrides = useTasksStore((s) => s.overrides);
  const loaded = useTasksStore((s) => s.loaded);
  const hydrate = useTasksStore((s) => s.hydrate);
  const applyFilters = useViewFilters();
  const dayCount = useSettingsStore((s) => s.calendarDayCount);
  const multiDayFocus = useSettingsStore((s) => s.multiDayFocus);
  const weekStartsOn = useSettingsStore((s) => s.weekStartsOn);
  const setDayCount = useSettingsStore((s) => s.setCalendarDayCount);
  useEffect(() => { if (!loaded) void hydrate(); }, [loaded, hydrate]);

  const normalizeStart = useCallback((d: Date) => startOfDay(d), []);
  const defaultStart = useCallback(
    () => multiDayFocusAnchor(new Date(), multiDayFocus, weekStartsOn),
    [multiDayFocus, weekStartsOn],
  );
  const [start, setStart] = useRememberedCalendarAnchor("days", defaultStart, normalizeStart);
  useMultiDayFocusRollover(setStart, multiDayFocus, weekStartsOn);
  const [inboxOpen, setInboxOpen] = useState(false);
  const mobile = useMobileUx();

  useCalendarNav("days", setStart);

  const setStep = useCalendarNavStore((s) => s.setStep);
  useEffect(() => {
    setStep((delta) => setStart((prev) => addDays(prev, delta * dayCount)));
    return () => setStep(null);
  }, [setStep, dayCount]);

  const filtered = useMemo(() => applyFilters(tasks), [applyFilters, tasks]);
  const { days, windowStart } = useExpandedWindow(start, dayCount, filtered, rules, overrides);

  return (
    <div className={`flex h-full min-h-0 flex-col ${mobile ? "" : "gap-4"}`}>
      <CalendarHeader
        anchor={start}
        onChange={setStart}
        onToday={() => setStart(multiDayFocusAnchor(new Date(), multiDayFocus, weekStartsOn))}
        unit="days"
        actions={<InboxToggle open={inboxOpen} onToggle={() => setInboxOpen((v) => !v)} />}
      />
      <div className="flex min-h-0 flex-1 gap-4">
        <div className="min-w-0 flex-1">
          <WeekGrid
            days={days}
            windowStart={windowStart}
            visibleStart={start}
            visibleCount={dayCount}
            onDayShift={(delta) => setStart((prev) => addDays(prev, delta))}
            onDayCountChange={(delta) => void setDayCount(dayCount + delta)}
          />
        </div>
        {inboxOpen && !mobile && (
          <aside className="w-64 shrink-0 rounded-md border bg-card p-2">
            <InboxPanel />
          </aside>
        )}
      </div>
      {mobile && <MobileInboxDrawer open={inboxOpen} onOpenChange={setInboxOpen} />}
    </div>
  );
}
